import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import {
  Box, Container, Grid, Card, CardHeader, CardContent, Typography,
  Hidden, AlertColor,
  Divider,
  IconButton,
  Alert,
  Toolbar,
  TextField,
  ListItemText,
  MenuItem,
  Stack,
  Chip
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import ErrorIcon from '@mui/icons-material/Error';
import CancelIcon from '@mui/icons-material/Cancel';
import ReportProblemIcon from '@mui/icons-material/ReportProblem';
import { connect } from 'react-redux';
import { hide, show } from '../store/actions/busyIndicatorActions';
import {
  showAlert as showGlobalAlert, clearAllAlerts as clearAllGlobalAlerts
} from '../store/actions/alertActions';
import {
  formatErrorMessage, formatDateToLongDate, getDaysFromNow
} from '../utils/common';
import AppConstants from '../constants/AppConstants';
import FleetIoService from '../services/FleetIoService';
import Table from '../components/Table';
import MobileDataList from '../components/MobileDataList';
import EnumService from '../services/EnumService';
import { EnumRes } from '../proxy/proxy';

interface Props {
  showAlert: (message: any) => any,
  clearAllAlerts: () => any,
  showBusyIndicator: () => any,
  hideBusyIndicator: () => any,
}

interface Warning {
  type: AlertColor;
  category: string;
  message: string;
}

const ServiceReminders: React.FC<Props> = ({
  showAlert,
  clearAllAlerts,
  showBusyIndicator,
  hideBusyIndicator
}) => {
  const [rows, setRows] = useState([]);
  const [page, setPage] = useState(0);
  const [sortModel, setSortModel] = useState([]);
  const [warningsData, setWarningsData] = useState<Warning[]>([]);
  const [allStates, setAllStates] = useState<Array<EnumRes>>([]);
  const [selectedState, setSelectedState] = useState<number>(1);
  const [selectedSummary, setSelectedSummary] = useState<string>('');
  const [counts, setCounts] = useState({
    fridgeBreached: 0,
    fridgeWarning: 0,
    standardBreached: 0,
    standardWarning: 0
  });

  const columns = [
    { field: 'id', headerName: 'ID', width: 90 },
    { field: 'license_plate', headerName: 'Vehicle', width: 150 },
    { field: 'service_task', headerName: 'Service Task', flex: 1 },
    { field: 'service_description', headerName: 'Service Description', flex: 1 },
    { field: 'next_due_at', headerName: 'Next Due', width: 180 },
    { field: 'due_soon_at', headerName: 'Due Soon', width: 180 },
    { field: 'status', headerName: 'Status', width: 120 },
  ];

  const renderEachMobileServiceCard = (row: any) => (
    <Card key={row.id} sx={{ marginBottom: '10px' }} variant="outlined">
      <CardHeader
        title={row.license_plate || 'No Plate Info'}
        subheader={`Status: ${row.status || 'N/A'}`}
      />
      <Divider />
      <CardContent>
        <Grid container spacing={1}>
          {row.service_task && (
            <Grid item xs={12}>
              <Typography variant="body2">
                <b>Task:</b> {row.service_task}
              </Typography>
            </Grid>
          )}
          {row.service_description && (
            <Grid item xs={12}>
              <Typography variant="body2">
                <b>Description:</b> {row.service_description}
              </Typography>
            </Grid>
          )}
          {row.next_due_at && (
            <Grid item xs={12}>
              <Typography variant="body2">
                <b>Next Due:</b> {row.next_due_at}
              </Typography>
            </Grid>
          )}
          {row.due_soon_at && (
            <Grid item xs={12}>
              <Typography variant="body2">
                <b>Due Soon:</b> {row.due_soon_at}
              </Typography>
            </Grid>
          )}
        </Grid>
      </CardContent>
    </Card>
  );

  const refreshData = () => {
    const payload = {
      actionId: 301,
      stateId: selectedState
    };

    FleetIoService.fleetIoPage(payload)
      .then((resp) => {
        const now = new Date();
        const warnings: Warning[] = [];
        let fridgeBreached = 0;
        let fridgeWarning = 0;
        let standardBreached = 0;
        let standardWarning = 0;

        const transformedRows = resp.records.map((record: any) => {
          const dueSoonAt = record?.due_soon_at ? new Date(record.due_soon_at) : null;
          const nextDueAt = record?.next_due_at ? new Date(record.next_due_at) : null;
          const serviceType = record?.service_task?.name?.toLowerCase().includes('fridge') ? 'fridge' : 'standard';
          let breached = false;
          let warning = false;

          if (nextDueAt && nextDueAt < now) {
            breached = true;
            const message = `${record.vehicle?.license_plate} ${record?.service_task?.name} is overdue by ${Math.abs(getDaysFromNow(record?.next_due_at))} days.`;
            warnings.push({ type: 'error', category: serviceType, message });
            if (serviceType === 'fridge') fridgeBreached++;
            else standardBreached++;
          } else if (dueSoonAt && dueSoonAt < now) {
            warning = true;
            const message = `${record.vehicle?.license_plate} ${record?.service_task?.name} due in ${getDaysFromNow(record?.next_due_at)} days.`;
            warnings.push({ type: 'warning', category: serviceType, message });
            if (serviceType === 'fridge') fridgeWarning++;
            else standardWarning++;
          }

          return {
            id: record.id,
            vehicle_name: record.vehicle?.name,
            license_plate: record.vehicle?.license_plate,
            make: record.vehicle?.make,
            model: record.vehicle?.model,
            service_task: record.service_task?.name,
            service_description: record.service_task?.description,
            next_due_at: formatDateToLongDate(record.next_due_at),
            due_soon_at: formatDateToLongDate(record.due_soon_at),
            status: record.service_reminder_status,
            breached,
            warning,
            serviceType
          };
        });

        setCounts({ fridgeBreached, fridgeWarning, standardBreached, standardWarning });
        setRows(transformedRows);
        setWarningsData(warnings);
        hideBusyIndicator();
      })
      .catch((err: any) => {
        showAlert(formatErrorMessage(JSON.parse(err.response)));
        setRows([]);
        setWarningsData([]);
        setCounts({ fridgeBreached: 0, fridgeWarning: 0, standardBreached: 0, standardWarning: 0 });
        hideBusyIndicator();
      });
  };

  useEffect(() => {
    showBusyIndicator();
    clearAllAlerts();
    refreshData();
    EnumService.getEnums('states').then((states) => {
      setAllStates(states);
    }).catch();
  }, []);

  useEffect(() => {
    if (selectedState !== null) {
      showBusyIndicator();
      clearAllAlerts();
      refreshData();
    }
  }, [selectedState]);

  const onStatesSelect = (event: any) => {
    setSelectedState(event?.target?.value || 1);
  };

  return (
    <>
      <Helmet>
        <title>Service Reminders | {AppConstants.APP_NAME}</title>
      </Helmet>
      <Box
        sx={{
          backgroundColor: 'background.default',
          minHeight: '100%',
          py: 3
        }}
      >
        <Container maxWidth="xl">
          <Card>
            <CardHeader
              title="Service Reminders"
              action={(
                <IconButton onClick={refreshData}>
                  <RefreshIcon />
                </IconButton>
              )}
            />
            <Divider />
            <CardContent>
              <Toolbar style={{ justifyContent: 'flex-end' }}>
                <TextField
                  sx={{ width: '200px', marginRight: '1rem' }}
                  size="small"
                  label="State"
                  name="state"
                  onChange={onStatesSelect}
                  onBlur={onStatesSelect}
                  select
                  value={selectedState}
                >
                  {allStates.map((option) => (
                    <MenuItem key={option.id} value={option.id}>
                      <ListItemText primary={option.value} />
                    </MenuItem>
                  ))}
                </TextField>
              </Toolbar>
              <Box
                sx={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: 2,
                  mb: 2,
                  marginTop: 2,
                  alignItems: 'center',
                  justifyContent: 'flex-start',
                }}
              >
                <Stack direction="row" spacing={0.5} alignItems="center">
                  <Chip
                    icon={<ErrorIcon style={{ color: '#d32f2f' }} fontSize="small" />}
                    label={(
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="body2" component="span" sx={{ fontWeight: 600 }}>
                          {counts.fridgeBreached}
                        </Typography>
                        <Typography
                          variant="body2"
                          component="span"
                          sx={{
                            ml: 0.5,
                            fontWeight: selectedSummary === 'Fridge Service Breached' ? 700 : 400,
                            cursor: 'pointer',
                          }}
                        >
                          Fridge Service
                        </Typography>
                      </Box>
                    )}
                    clickable
                    onClick={() => setSelectedSummary('Fridge Service Breached')}
                    onDelete={selectedSummary === 'Fridge Service Breached' ? () => setSelectedSummary('') : undefined}
                    deleteIcon={selectedSummary === 'Fridge Service Breached' ? <CancelIcon fontSize="small" /> : undefined}
                    sx={{
                      height: 36,
                      borderRadius: 18,
                      border: 'none',
                      transition: 'all 0.2s ease',
                      fontWeight: selectedSummary === 'Fridge Service Breached' ? 700 : 400,
                    }}
                    variant={selectedSummary === 'Fridge Service Breached' ? 'filled' : 'outlined'}
                  />
                </Stack>
                <Stack direction="row" spacing={0.5} alignItems="center">
                  <Chip
                    icon={<ReportProblemIcon style={{ color: '#ffa000' }} fontSize="small" />}
                    label={(
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="body2" component="span" sx={{ fontWeight: 600 }}>
                          {counts.fridgeWarning}
                        </Typography>
                        <Typography
                          variant="body2"
                          component="span"
                          sx={{
                            ml: 0.5,
                            fontWeight: selectedSummary === 'Fridge Service Warning' ? 700 : 400,
                            cursor: 'pointer',
                          }}
                        >
                          Fridge Service
                        </Typography>
                      </Box>
                    )}
                    clickable
                    onClick={() => setSelectedSummary('Fridge Service Warning')}
                    onDelete={selectedSummary === 'Fridge Service Warning' ? () => setSelectedSummary('') : undefined}
                    deleteIcon={selectedSummary === 'Fridge Service Warning' ? <CancelIcon fontSize="small" /> : undefined}
                    sx={{
                      height: 36,
                      borderRadius: 18,
                      border: 'none',
                      transition: 'all 0.2s ease',
                      fontWeight: selectedSummary === 'Fridge Service Warning' ? 700 : 400,
                    }}
                    variant={selectedSummary === 'Fridge Service Warning' ? 'filled' : 'outlined'}
                  />
                </Stack>
                {/* Standard service */}
                <Stack direction="row" spacing={0.5} alignItems="center">
                  <Chip
                    icon={<ErrorIcon style={{ color: '#d32f2f' }} fontSize="small" />}
                    label={(
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="body2" component="span" sx={{ fontWeight: 600 }}>
                          {counts.standardBreached}
                        </Typography>
                        <Typography
                          variant="body2"
                          component="span"
                          sx={{
                            ml: 0.5,
                            fontWeight: selectedSummary === 'Standard Service Breached' ? 700 : 400,
                            cursor: 'pointer',
                          }}
                        >
                          Standard Service
                        </Typography>
                      </Box>
                    )}
                    clickable
                    onClick={() => setSelectedSummary('Standard Service Breached')}
                    onDelete={selectedSummary === 'Standard Service Breached' ? () => setSelectedSummary('') : undefined}
                    deleteIcon={selectedSummary === 'Standard Service Breached' ? <CancelIcon fontSize="small" /> : undefined}
                    sx={{
                      height: 36,
                      borderRadius: 18,
                      border: 'none',
                      transition: 'all 0.2s ease',
                      fontWeight: selectedSummary === 'Standard Service Breached' ? 700 : 400,
                    }}
                    variant={selectedSummary === 'Standard Service Breached' ? 'filled' : 'outlined'}
                  />
                </Stack>
                <Stack direction="row" spacing={0.5} alignItems="center">
                  <Chip
                    icon={<ReportProblemIcon style={{ color: '#ffa000' }} fontSize="small" />}
                    label={(
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="body2" component="span" sx={{ fontWeight: 600 }}>
                          {counts.standardWarning}
                        </Typography>
                        <Typography
                          variant="body2"
                          component="span"
                          sx={{
                            ml: 0.5,
                            fontWeight: selectedSummary === 'Standard Service Warning' ? 700 : 400,
                            cursor: 'pointer',
                          }}
                        >
                          Standard Service
                        </Typography>
                      </Box>
                    )}
                    clickable
                    onClick={() => setSelectedSummary('Standard Service Warning')}
                    onDelete={selectedSummary === 'Standard Service Warning' ? () => setSelectedSummary('') : undefined}
                    deleteIcon={selectedSummary === 'Standard Service Warning' ? <CancelIcon fontSize="small" /> : undefined}
                    sx={{
                      height: 36,
                      borderRadius: 18,
                      border: 'none',
                      transition: 'all 0.2s ease',
                      fontWeight: selectedSummary === 'Standard Service Warning' ? 700 : 400,
                    }}
                    variant={selectedSummary === 'Standard Service Warning' ? 'filled' : 'outlined'}
                  />
                </Stack>
              </Box>
              {/* Filter rows and warnings based on selectedSummary */}
              {(() => {
                let filteredRows = rows;
                let filteredWarnings = warningsData;
                if (selectedSummary === 'Fridge Service Breached') {
                  filteredRows = rows.filter((r: any) => r.serviceType === 'fridge' && r.breached);
                  filteredWarnings = warningsData.filter((w) => w.category === 'fridge' && w.type === 'error');
                } else if (selectedSummary === 'Fridge Service Warning') {
                  filteredRows = rows.filter((r: any) => r.serviceType === 'fridge' && r.warning);
                  filteredWarnings = warningsData.filter((w) => w.category === 'fridge' && w.type === 'warning');
                } else if (selectedSummary === 'Standard Service Breached') {
                  filteredRows = rows.filter((r: any) => r.serviceType === 'standard' && r.breached);
                  filteredWarnings = warningsData.filter((w) => w.category === 'standard' && w.type === 'error');
                } else if (selectedSummary === 'Standard Service Warning') {
                  filteredRows = rows.filter((r: any) => r.serviceType === 'standard' && r.warning);
                  filteredWarnings = warningsData.filter((w) => w.category === 'standard' && w.type === 'warning');
                }
                return (
                  <>
                    {/* <Box sx={{ mt: 2, mb: 4 }}>
                      {filteredWarnings?.map((alert) => (
                        <Alert sx={{ margin: 1 }} key={alert.message} severity={alert.type}>
                          {alert.message}
                        </Alert>
                      ))}
                    </Box> */}
                    <Hidden mdDown>
                      <Table
                        rows={filteredRows}
                        columns={columns}
                        page={page}
                        getRowClassName={(params: any) => (params?.row.breached && 'error') || (params?.row.warning && 'warning') || ''}
                        totalRows={filteredRows.length}
                        sortModel={sortModel}
                        onPageChange={(newPage) => setPage(newPage)}
                        onSortModelChange={(model) => setSortModel(model)}
                      />
                    </Hidden>
                    <Hidden mdUp>
                      <MobileDataList
                        rows={filteredRows}
                        renderEachRow={renderEachMobileServiceCard}
                        totalRows={filteredRows.length}
                        page={page}
                        onPageChange={(newPage: any) => setPage(newPage)}
                      />
                    </Hidden>
                  </>
                );
              })()}
            </CardContent>
          </Card>
        </Container>
      </Box>
    </>
  );
};

const mapStateToProps = null;

const mapDispatchToProps = (dispatch: any) => ({
  showBusyIndicator: () => dispatch(show()),
  hideBusyIndicator: () => dispatch(hide()),
  showAlert: (message: any) => dispatch(showGlobalAlert(message)),
  clearAllAlerts: () => dispatch(clearAllGlobalAlerts())
});

export default connect(mapStateToProps, mapDispatchToProps)(ServiceReminders);
